import { Button } from '@design-system';
import { useTranslation } from 'next-i18next';
import React, { useCallback } from 'react';

import type { ProductFiltersController } from '../../hooks/useProductFilters';
import type {
  FinishFilter,
  MediumFilter,
  ReleaseFilter,
} from '../../types/Filters';
import {
  TrackAllFiltersCleared,
  TrackFiltersSelected,
} from '../../utils/analytics';
import Drawer from '../Layout/Drawer';
import FilterCheckboxGroup from './FilterCheckboxGroup';

type Props = {
  shown: boolean;
  productCount: number;
  filters: ProductFiltersController;
  setShown: (value: boolean) => void;
};

const ProductFiltersDrawer: React.FC<Props> = ({
  filters,
  productCount,
  setShown,
  shown,
}) => {
  const { t } = useTranslation('filters');

  const close = useCallback(() => {
    setShown(false);
  }, [setShown]);

  return (
    <Drawer onClose={close} shown={shown}>
      <div className="w-full space-y-6">
        <div className="flex flex-row items-center justify-center space-x-3">
          <div className="flex-1">
            <p className="text-md font-bold md:text-lg">{t('filters')}</p>
          </div>
          <div className="shrink-0">
            <Button
              label={t('clearAllFilters')}
              onClick={() => {
                filters.clearAll();

                TrackAllFiltersCleared({
                  page: 'works index',
                  location: 'filter drawer',
                });
              }}
              size="md"
              variant="tertiary"
            />
          </div>
        </div>

        <div className="space-y-3">
          <span className="text-neutral-4">{t('products.medium')}</span>
          <FilterCheckboxGroup<MediumFilter>
            filter={filters.mediums}
            onChange={() => filters.setIsInteractedWith(true)}
            options={[
              {
                value: 'Print editions',
                label: t('products.mediumPrints'),
              },
              {
                value: 'Sculpture editions',
                label: t('products.mediumSculptures'),
              },
              {
                value: 'Studio works',
                label: t('products.mediumStudioWorks'),
              },
              {
                value: 'Collectibles',
                label: t('products.mediumCollectibles'),
              },
            ]}
          />
        </div>

        <div className="space-y-3">
          <span className="text-neutral-4">{t('products.finish')}</span>
          <FilterCheckboxGroup<FinishFilter>
            filter={filters.finish}
            onChange={() => filters.setIsInteractedWith(true)}
            options={[
              {
                value: 'Hand-finished',
                label: t('products.finishHandFinished'),
              },
            ]}
          />
        </div>

        <div className="space-y-3">
          <span className="text-neutral-4">{t('products.release')}</span>

          <FilterCheckboxGroup<ReleaseFilter>
            filter={filters.release}
            onChange={() => filters.setIsInteractedWith(true)}
            options={[
              {
                value: 'Standard release',
                label: t('products.releaseBuyNow'),
              },
              {
                value: 'Draw release',
                label: t('products.releaseDrawRelease'),
              },
              {
                value: 'Timed release',
                label: t('products.releaseTimedRelease'),
              },
              {
                value: 'Available by enquiry',
                label: t('products.releaseEnquire'),
              },
            ]}
          />
        </div>

        <div className="sticky bottom-0 !mt-12 bg-white pb-6">
          <div className="mx-auto w-full max-w-md">
            <Button
              fullWidth
              label={t('products.showProducts', { count: productCount })}
              onClick={() => {
                close();
                TrackFiltersSelected({
                  page: 'works index',
                  indexAvailabilityFilter: [filters.availability.value],
                  indexFinishingFilter: filters.finish.values,
                  indexMediumFilter: filters.mediums.values,
                  indexReleaseFilter: filters.release.values,
                  indexOrderFilter: filters.order.value,
                  indexSearchQuery: filters.searchTerm.value,
                  indexLength: productCount,
                });
              }}
              variant="primary"
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default ProductFiltersDrawer;
